import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import CommentIcon from '@mui/icons-material/Comment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Sidebar from './Sidebar';
import { useUser } from '../contexts/UserContext';
import { supabase } from '../lib/supabase';
import Loading from './Loading';

type Question = {
  id: string;
  title: string;
  body: string;
  votes: number;
  comments: number;
};

type CommentType = {
  id: string;
  body: string;
  user_id: string;
  created_at: string;
  user_name?: string;
};

type Voter = {
  id: string;
  name: string;
  value: number;
};

export default function Forum() {
  const { profile } = useUser();
  const [search, setSearch] = useState('');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Question | null>(null);
  const [comments, setComments] = useState<CommentType[]>([]);
  const [comment, setComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [voters, setVoters] = useState<Voter[]>([]);

  useEffect(() => {
    const fetchQuestions = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('questions')
        .select('id, title, body, votes, comments:comments(id)')
        .order('created_at', { ascending: false });
      if (data) {
        setQuestions(
          data.map((q: any) => ({
            ...q,
            comments: q.comments ? q.comments.length : 0,
          }))
        );
      }
      setLoading(false);
    };
    fetchQuestions();
  }, []);

  // Fuzzy search
  const filtered = questions.filter(q =>
    q.title.toLowerCase().includes(search.toLowerCase()) ||
    q.body.toLowerCase().includes(search.toLowerCase())
  );

  // Fetch comments for selected question
  useEffect(() => {
    if (!selected) return;
    const fetchComments = async () => {
      setCommentLoading(true);
      const { data } = await supabase
        .from('comments')
        .select('id, body, user_id, created_at, users(name)')
        .eq('question_id', selected.id)
        .order('created_at', { ascending: true });
      setComments(
        (data || []).map((c: any) => ({
          ...c,
          user_name: c.users?.name || 'Unknown',
        }))
      );
      setCommentLoading(false);
    };
    fetchComments();
  }, [selected]);

  // Fetch voters for selected question
  useEffect(() => {
    if (!selected) return;
    const fetchVoters = async () => {
      const { data } = await supabase
        .from('votes')
        .select('user_id, value, users(name, id)')
        .eq('question_id', selected.id);
      setVoters(
        (data || []).map((v: any) => ({
          id: v.users?.id || v.user_id,
          name: v.users?.name || 'Unknown',
          value: v.value,
        }))
      );
    };
    fetchVoters();
  }, [selected, comments]);

  // Upvote/downvote
  const handleVote = async (question: Question, value: 1 | -1) => {
    if (!profile) return;
    const { data: existingVote, error: voteFetchError } = await supabase
      .from('votes')
      .select('value')
      .eq('user_id', profile.id)
      .eq('question_id', question.id)
      .maybeSingle();
    if (voteFetchError) {
      alert('Error fetching vote: ' + voteFetchError.message);
      return;
    }
    let voteError;
    if (existingVote) {
      if (existingVote.value === value) {
        const { error } = await supabase
          .from('votes')
          .delete()
          .eq('user_id', profile.id)
          .eq('question_id', question.id);
        voteError = error;
      } else {
        const { error } = await supabase.from('votes').upsert({
          user_id: profile.id,
          question_id: question.id,
          value,
        }, { onConflict: 'user_id,question_id' });
        voteError = error;
      }
    } else {
      const { error } = await supabase.from('votes').upsert({
        user_id: profile.id,
        question_id: question.id,
        value,
      }, { onConflict: 'user_id,question_id' });
      voteError = error;
    }
    if (voteError) {
      console.log('Vote error: ' + voteError.message);
      return;
    }
    const { error: rpcError } = await supabase.rpc('update_votes_count', { qid: question.id });
    if (rpcError) {
      console.log('Vote count update error: ' + rpcError.message);
      return;
    }
    // Refresh questions
    const { data, error: fetchError } = await supabase
      .from('questions')
      .select('id, title, body, votes, comments:comments(id)')
      .order('created_at', { ascending: false });
    if (fetchError) {
      console.log('Fetch error: ' + fetchError.message);
      return;
    }
    if (data) {
      setQuestions(
        data.map((q: any) => ({
          ...q,
          comments: q.comments ? q.comments.length : 0,
        }))
      );
    }
  };

  // Post comment
  const handlePostComment = async () => {
    if (!selected || !profile || !comment.trim()) return;
    await supabase.from('comments').insert({
      question_id: selected.id,
      user_id: profile.id,
      body: comment.trim(),
    });
    setComment('');
    // Refresh comments
    const { data } = await supabase
      .from('comments')
      .select('id, body, user_id, created_at')
      .eq('question_id', selected.id)
      .order('created_at', { ascending: true });
    setComments(data || []);
  };

  if (loading) return <Loading />;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Sidebar user={profile || { name: '', email: '', role: '', avatar_url: '', is_premium: false }} />
      <Box sx={{ flexGrow: 1, p: 4 }}>
        <Typography variant="h4" fontWeight={700} mb={3}>Forum</Typography>
        <TextField
          label="Search questions"
          value={search}
          onChange={e => setSearch(e.target.value)}
          fullWidth
          sx={{ mb: 3 }}
        />
        {filtered.map(q => (
          <Card key={q.id} sx={{ mb: 3, borderRadius: 3 }}>
            <CardHeader
              title={<Typography variant="h6">{q.title}</Typography>}
              subheader={<Typography variant="body2" color="text.secondary">{q.body}</Typography>}
              action={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton color="primary" onClick={() => handleVote(q, 1)}><ThumbUpIcon /></IconButton>
                  <Typography>{q.votes}</Typography>
                  <IconButton color="secondary" onClick={() => handleVote(q, -1)}><ThumbDownIcon /></IconButton>
                  <IconButton onClick={() => setSelected(q)}><CommentIcon /></IconButton>
                  <Typography>{q.comments}</Typography>
                </Box>
              }
            />
          </Card>
        ))}
        {/* Question details and comments dialog */}
        <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
          <DialogTitle>{selected?.title}</DialogTitle>
          <DialogContent>
            <Typography mb={2}>{selected?.body}</Typography>
            {commentLoading ? <Loading /> : (
              <List>
                {comments.map(c => (
                  <ListItem key={c.id}>
                    <ListItemText primary={c.body} secondary={c.user_name} />
                  </ListItem>
                ))}
              </List>
            )}
            <Box mt={2}>
              <Typography fontWeight={700} fontSize={13}>Upvoted by:</Typography>
              {voters.filter(v => v.value === 1).map(v => (
                <Typography key={v.id} fontSize={13}>{v.name}</Typography>
              ))}
              <Typography fontWeight={700} fontSize={13} mt={1}>Downvoted by:</Typography>
              {voters.filter(v => v.value === -1).map(v => (
                <Typography key={v.id} fontSize={13}>{v.name}</Typography>
              ))}
            </Box>
            <TextField
              label="Add a comment"
              value={comment}
              onChange={e => setComment(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelected(null)}>Close</Button>
            <Button variant="contained" onClick={handlePostComment}>Post Comment</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
} 